import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { Plus, Check, Trash2, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { CollectionReminders } from '../components/CollectionReminders';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountsPage = () => (
  <Layout>
    <AccountsContent />
  </Layout>
);

export const AccountsContent = ({ showOnly = null, hideHeader = false } = {}) => {
  const [tab, setTab] = useState(showOnly || 'payables');
  const [payables, setPayables] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPayModal, setShowPayModal] = useState(null);
  const [payItem, setPayItem] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: '', supplier_name: '', customer_id: '', customer_name: '',
    description: '', amount: '', due_date: '', category: 'mercadoria', notes: ''
  });

  useEffect(() => { load(); }, [tab]);

  const load = async () => {
    try {
      if (tab === 'payables') {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/accounts/payables`, { withCredentials: true }),
          axios.get(`${API}/suppliers`, { withCredentials: true })
        ]);
        setPayables(pRes.data);
        setSuppliers(sRes.data);
      } else {
        const [rRes, cRes] = await Promise.all([
          axios.get(`${API}/accounts/receivables`, { withCredentials: true }),
          axios.get(`${API}/customers`, { withCredentials: true })
        ]);
        setReceivables(rRes.data);
        setCustomers(cRes.data);
      }
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        due_date: new Date(formData.due_date).toISOString(),
        notes: formData.notes
      };
      if (tab === 'payables') {
        payload.supplier_id = formData.supplier_id || null;
        payload.supplier_name = formData.supplier_name || (suppliers.find(s => s.id === formData.supplier_id)?.name || 'Avulso');
        payload.category = formData.category;
        await axios.post(`${API}/accounts/payables`, payload, { withCredentials: true });
      } else {
        payload.customer_id = formData.customer_id || null;
        payload.customer_name = formData.customer_name || (customers.find(c => c.id === formData.customer_id)?.name || 'Avulso');
        await axios.post(`${API}/accounts/receivables`, payload, { withCredentials: true });
      }
      load();
      resetForm();
    } catch (err) {
      setError(extractErrorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  // Extrai mensagem legível de qualquer erro da API (Pydantic 422 array, HTTPException string, network error)
  const extractErrorMsg = (err) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d.msg || JSON.stringify(d)).join(' | ');
    }
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (err?.message) return err.message;
    return 'Erro ao processar a operação. Tente novamente.';
  };

  const handlePay = async () => {
    setError('');
    if (!showPayModal) return;
    setLoading(true);
    try {
      if (tab === 'payables') {
        await axios.post(`${API}/accounts/payables/${showPayModal}/pay`, { payment_method: paymentMethod }, { withCredentials: true });
      } else {
        const outstanding = payItem ? (payItem.total_with_interest || payItem.amount) : 0;
        const amt = parseFloat(payAmount);
        const body = { payment_method: paymentMethod };
        // Se o operador informou um valor menor que o total, envia como pagamento parcial
        if (!isNaN(amt) && amt > 0 && amt < outstanding - 0.01) {
          body.amount = amt;
        }
        await axios.post(`${API}/accounts/receivables/${showPayModal}/receive`, body, { withCredentials: true });
      }
      setShowPayModal(null);
      setPayItem(null);
      setPayAmount('');
      load();
    } catch (err) {
      setError(extractErrorMsg(err));
    }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta conta?')) return;
    try {
      const url = tab === 'payables' ? `${API}/accounts/payables/${id}` : `${API}/accounts/receivables/${id}`;
      await axios.delete(url, { withCredentials: true });
      load();
    } catch (err) { setError(extractErrorMsg(err)); }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '', supplier_name: '', customer_id: '', customer_name: '',
      description: '', amount: '', due_date: '', category: 'mercadoria', notes: ''
    });
    setShowForm(false);
    setError('');
  };

  const items = tab === 'payables' ? payables : receivables;
  const totalPendente = items.filter(i => i.status === 'pendente').reduce((s, i) => s + i.amount, 0);
  const totalVencido = items.filter(i => i.status === 'vencido').reduce((s, i) => s + i.amount, 0);
  const totalPago = items.filter(i => i.status === 'pago' || i.status === 'recebido').reduce((s, i) => s + i.amount, 0);

  const statusBadge = (status) => {
    if (status === 'vencido') return <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 font-bold flex items-center gap-1 w-fit"><AlertTriangle size={12} />Vencido</span>;
    if (status === 'pago' || status === 'recebido') return <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-bold flex items-center gap-1 w-fit"><CheckCircle2 size={12} />{status === 'pago' ? 'Pago' : 'Recebido'}</span>;
    return <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-bold flex items-center gap-1 w-fit"><Clock size={12} />Pendente</span>;
  };

  return (
    <>
      <div className="p-8" data-testid="accounts-page">
        {!hideHeader && <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-8">Contas Financeiras</h1>}

        {!showOnly && (
        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList className="grid w-full md:w-[34rem] grid-cols-3">
            <TabsTrigger value="payables" data-testid="tab-payables">Contas a Pagar</TabsTrigger>
            <TabsTrigger value="receivables" data-testid="tab-receivables">Contas a Receber</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">Cobranças</TabsTrigger>
          </TabsList>
        </Tabs>
        )}

        {tab === 'reminders' && <CollectionReminders />}

        {tab !== 'reminders' && (<>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Pendentes</p>
            <p className="text-2xl font-bold text-yellow-700 mt-1">R$ {totalPendente.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Vencidos</p>
            <p className="text-2xl font-bold text-red-600 mt-1">R$ {totalVencido.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-md border border-slate-200 p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{tab === 'payables' ? 'Pagos' : 'Recebidos'}</p>
            <p className="text-2xl font-bold text-green-600 mt-1">R$ {totalPago.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex justify-end mb-4">
          <Button onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-account-btn">
            <Plus size={20} className="mr-2" /> Nova Conta a {tab === 'payables' ? 'Pagar' : 'Receber'}
          </Button>
        </div>

        {showForm && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Nova Conta</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tab === 'payables' ? (
                <>
                  <div className="space-y-2">
                    <Label>Fornecedor</Label>
                    <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v, supplier_name: suppliers.find(s => s.id === v)?.name || '' })}>
                      <SelectTrigger><SelectValue placeholder="Selecione ou deixe avulso" /></SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      value={formData.supplier_name}
                      onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                      placeholder="Ou digite o nome (avulso)"
                      className="mt-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mercadoria">Mercadoria</SelectItem>
                        <SelectItem value="despesa_fixa">Despesa Fixa</SelectItem>
                        <SelectItem value="servico">Serviço</SelectItem>
                        <SelectItem value="outros">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div className="space-y-2 md:col-span-2">
                  <Label>Cliente</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v, customer_name: customers.find(c => c.id === v)?.name || '' })}>
                    <SelectTrigger><SelectValue placeholder="Selecione ou deixe avulso" /></SelectTrigger>
                    <SelectContent>
                      {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Ou digite o nome (avulso)"
                    className="mt-2"
                  />
                </div>
              )}
              <div className="space-y-2 md:col-span-2">
                <Label>Descrição *</Label>
                <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Data de Vencimento *</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} required />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Observações</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              {error && <div className="md:col-span-2"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>}
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Salvando...' : 'Salvar'}</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Vencimento</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{tab === 'payables' ? 'Fornecedor' : 'Cliente'}</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Descrição</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Valor</th>
                {tab === 'receivables' && (
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Juros</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {items.length === 0 && <tr><td colSpan={tab === 'receivables' ? 7 : 6} className="text-center py-8 text-slate-400">Nenhuma conta encontrada</td></tr>}
              {items.map((item) => {
                const done = item.status === 'pago' || item.status === 'recebido';
                const hasInterest = tab === 'receivables' && item.interest > 0;
                return (
                  <tr key={item.id} className={done ? 'bg-green-50' : 'hover:bg-slate-50'}>
                    <td className="px-4 py-3 text-sm">
                      {new Date(item.due_date).toLocaleDateString('pt-BR')}
                      {tab === 'receivables' && item.days_overdue > 0 && !done && (
                        <div className="text-xs text-red-600 font-bold">{item.days_overdue} dias atraso</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{tab === 'payables' ? item.supplier_name : item.customer_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.description}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">R$ {item.amount.toFixed(2)}</td>
                    {tab === 'receivables' && (
                      <td className="px-4 py-3 text-right">
                        {hasInterest ? (
                          <div>
                            <div className="font-bold text-red-600">+ R$ {item.interest.toFixed(2)}</div>
                            <div className="text-xs text-slate-500">Total: R$ {item.total_with_interest?.toFixed(2)}</div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3">{statusBadge(item.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {!done && (
                          <button onClick={() => { setShowPayModal(item.id); setPayItem(item); setPayAmount((item.total_with_interest || item.amount || 0).toFixed(2)); setPaymentMethod('dinheiro'); }} className="text-green-600 hover:text-green-700 font-medium text-sm flex items-center gap-1" data-testid={`pay-${item.id}`}>
                            <Check size={16} /> {tab === 'payables' ? 'Pagar' : 'Receber'}
                          </button>
                        )}
                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      {/* Modal pagamento */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(() => { setShowPayModal(null); setPayItem(null); setPayAmount(''); setError(''); })}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-slate-900 mb-4">{tab === 'payables' ? 'Pagar Conta' : 'Receber'}</h3>
            {tab === 'receivables' && payItem && (() => {
              const outstanding = payItem.total_with_interest || payItem.amount;
              const amt = parseFloat(payAmount);
              const isPartial = !isNaN(amt) && amt > 0 && amt < outstanding - 0.01;
              const remaining = isPartial ? outstanding - amt : 0;
              return (
                <div className="space-y-2 mb-4">
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-bold text-slate-900">{payItem.customer_name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Total devido</span><span className="font-bold text-red-600">R$ {outstanding.toFixed(2)}</span></div>
                  </div>
                  <Label>Valor a receber *</Label>
                  <Input type="number" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="receive-amount-input" />
                  {isPartial && (
                    <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-800 font-medium" data-testid="partial-hint">
                      💰 Pagamento parcial — saldo restante ficará R$ {remaining.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="space-y-2 mb-4">
              <Label>Forma de Pagamento *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="pix">PIX Loja</SelectItem>
                  <SelectItem value="pix_maquininha">PIX na Maquininha</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && (
              <div className="mb-3" data-testid="pay-modal-error">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="font-semibold text-sm">{error}</AlertDescription>
                </Alert>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowPayModal(null); setPayItem(null); setPayAmount(''); setError(''); }} className="flex-1">Cancelar</Button>
              <Button onClick={handlePay} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-pay">
                {loading ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
